import { useState, useEffect, useCallback } from 'react';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Label } from '../../components/ui/label';
import { DollarSign, TrendingUp, TrendingDown, BarChart3, Shield, Banknote, Building2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function TransportPnL() {
  const [pnl, setPnl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedMonth, setSelectedMonth] = useState('');

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };
  const formatCurrency = (amount) => new Intl.NumberFormat('en-ZA', { style: 'currency', currency: 'ZAR' }).format(amount || 0);
  const formatMonthFull = (m) => {
    if (!m) return '';
    const [y, mo] = m.split('-');
    const names = ['', 'January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
    return `${names[parseInt(mo)] || mo} ${y}`;
  };

  const fetchData = useCallback(async () => {
    try {
      const params = {};
      if (selectedMonth) params.month = selectedMonth;
      const res = await axios.get(`${API_URL}/api/transport-pnl`, { headers, params });
      setPnl(res.data);
      if (!selectedMonth) setSelectedMonth(res.data.month);
    } catch { toast.error('Failed to load P&L'); }
    finally { setLoading(false); }
  }, [selectedMonth]);

  useEffect(() => { fetchData(); }, [selectedMonth]);

  if (loading) return <DashboardLayout><div className="flex items-center justify-center h-64 text-slate-600">Loading...</div></DashboardLayout>;
  if (!pnl) return <DashboardLayout><p className="text-slate-600 text-center py-12">No data available.</p></DashboardLayout>;

  const profitColor = pnl.operating_profit >= 0 ? 'text-emerald-700' : 'text-red-400';
  const netColor = pnl.net_profit >= 0 ? 'text-teal-400' : 'text-red-400';

  return (
    <DashboardLayout>
      <div className="space-y-5">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <BarChart3 className="h-6 w-6 text-teal-400" /> JSM Transport P&L
            </h1>
            <p className="text-slate-600 text-sm">Profit & Loss Statement</p>
          </div>
          <div className="min-w-[180px]">
            <Select value={selectedMonth} onValueChange={setSelectedMonth}>
              <SelectTrigger className="bg-white border-slate-300 text-slate-900" data-testid="pnl-month-filter"><SelectValue placeholder="Month" /></SelectTrigger>
              <SelectContent className="bg-white border-slate-200">
                {(pnl?.available_months || []).map(m => <SelectItem key={m} value={m}>{formatMonthFull(m)}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Top Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Card className="bg-gradient-to-br from-emerald-500 to-emerald-600 border-0 text-white">
            <CardContent className="p-3">
              <div className="flex items-center gap-1 mb-1"><TrendingUp className="h-4 w-4" /><span className="text-xs font-medium">Revenue</span></div>
              <p className="text-xl font-bold" data-testid="pnl-revenue">{formatCurrency(pnl.revenue)}</p>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-rose-500 to-rose-600 border-0 text-white">
            <CardContent className="p-3">
              <div className="flex items-center gap-1 mb-1"><TrendingDown className="h-4 w-4" /><span className="text-xs font-medium">Expenses</span></div>
              <p className="text-xl font-bold" data-testid="pnl-expenses">{formatCurrency(pnl.expenses)}</p>
            </CardContent>
          </Card>
          <Card className={`bg-gradient-to-br ${pnl.operating_profit >= 0 ? 'from-blue-500 to-blue-600' : 'from-red-700 to-red-800'} border-0 text-white`}>
            <CardContent className="p-3">
              <div className="flex items-center gap-1 mb-1"><DollarSign className="h-4 w-4" /><span className="text-xs font-medium">Operating Profit</span></div>
              <p className="text-xl font-bold" data-testid="pnl-operating">{formatCurrency(pnl.operating_profit)}</p>
            </CardContent>
          </Card>
          <Card className={`bg-gradient-to-br ${pnl.net_profit >= 0 ? 'from-teal-600 to-teal-700' : 'from-red-700 to-red-800'} border-0 text-white`}>
            <CardContent className="p-3">
              <div className="flex items-center gap-1 mb-1"><Banknote className="h-4 w-4" /><span className="text-xs font-medium">Net Profit (after tax)</span></div>
              <p className="text-xl font-bold" data-testid="pnl-net">{formatCurrency(pnl.net_profit)}</p>
            </CardContent>
          </Card>
        </div>

        {/* P&L Statement */}
        <Card className="bg-white border-slate-200 shadow-sm">
          <CardHeader><CardTitle className="text-slate-900 flex items-center gap-2"><BarChart3 className="h-5 w-5 text-teal-400" /> INCOME STATEMENT — {formatMonthFull(pnl.month)}{pnl.fy_label ? ` (FY ${pnl.fy_label})` : ''}</CardTitle></CardHeader>
          <CardContent>
            <div className="space-y-2">
              {/* Revenue */}
              <div className="flex justify-between items-center py-3 border-b border-slate-200">
                <div>
                  <span className="text-slate-900 text-lg font-semibold block">Revenue (Services Ex VAT)</span>
                  {(pnl.fy_revenue > 0 || pnl.manual_revenue > 0) && (
                    <div className="flex gap-3 mt-1">
                      {pnl.fy_revenue > 0 && <span className="text-xs text-cyan-700 font-medium">Bank Statements: {formatCurrency(pnl.fy_revenue)}</span>}
                      {pnl.manual_revenue > 0 && <span className="text-xs text-emerald-700 font-medium">Manual: {formatCurrency(pnl.manual_revenue)}</span>}
                    </div>
                  )}
                </div>
                <span className="text-emerald-700 font-bold text-xl">{formatCurrency(pnl.revenue)}</span>
              </div>

              {/* Expenses */}
              <div className="py-2">
                <div className="flex justify-between items-center py-2 border-b border-slate-200">
                  <div>
                    <span className="text-slate-900 text-lg font-semibold block">Less: Operating Expenses</span>
                    {(pnl.fy_expenses > 0 || pnl.manual_expenses > 0) && (
                      <div className="flex gap-3 mt-1">
                        {pnl.fy_expenses > 0 && <span className="text-xs text-cyan-700 font-medium">Bank Statements: {formatCurrency(pnl.fy_expenses)}</span>}
                        {pnl.manual_expenses > 0 && <span className="text-xs text-rose-300 font-medium">Manual: {formatCurrency(pnl.manual_expenses)}</span>}
                      </div>
                    )}
                  </div>
                  <span className="text-rose-700 font-bold text-xl">{formatCurrency(pnl.expenses)}</span>
                </div>
                {/* Expense breakdown */}
                <div className="pl-4 space-y-1 py-2">
                  {Object.entries(pnl.expense_breakdown || {}).map(([cat, amt]) => (
                    <div key={cat} className="flex justify-between items-center py-1">
                      <span className="text-slate-900 text-sm">{cat}</span>
                      <span className="text-slate-900 text-sm font-medium">{formatCurrency(amt)}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Operating Profit */}
              <div className={`flex justify-between items-center py-4 px-4 rounded-lg ${pnl.operating_profit >= 0 ? 'bg-blue-900/30 border border-blue-600' : 'bg-red-900/30 border border-red-600'}`}>
                <div>
                  <span className="text-slate-900 text-lg font-black block">OPERATING PROFIT</span>
                  <span className="text-slate-600 text-xs">Revenue - Expenses</span>
                </div>
                <span className={`font-black text-2xl ${profitColor}`} data-testid="pnl-op-profit">{formatCurrency(pnl.operating_profit)}</span>
              </div>

              <div className="h-2" />

              {/* Tax */}
              <div className="flex justify-between items-center py-3 border-b border-slate-200">
                <div className="flex items-center gap-2">
                  <Building2 className="h-4 w-4 text-amber-700" />
                  <span className="text-slate-900">Less: Company Tax ({pnl.tax_rate}%)</span>
                </div>
                <span className="text-amber-700 font-bold">{formatCurrency(pnl.tax_amount)}</span>
              </div>

              {/* Net Profit */}
              <div className={`flex justify-between items-center py-4 px-4 rounded-lg ${pnl.net_profit >= 0 ? 'bg-teal-900/50 border-2 border-teal-500' : 'bg-red-900/50 border-2 border-red-500'}`}>
                <div>
                  <span className="text-slate-900 text-xl font-black block">NET PROFIT AFTER TAX</span>
                  <span className="text-slate-600 text-xs">Operating Profit - 27% Tax</span>
                </div>
                <span className={`font-black text-3xl ${netColor}`} data-testid="pnl-net-profit">{formatCurrency(pnl.net_profit)}</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Director's Draw & Cash Advice */}
        <div className="grid md:grid-cols-2 gap-5">
          <Card className={`border-0 text-white ${pnl.safe_director_draw > 0 ? 'bg-gradient-to-br from-indigo-800 to-indigo-900' : 'bg-slate-100 border border-slate-200'}`}>
            <CardHeader className="pb-2"><CardTitle className="text-slate-900 flex items-center gap-2 text-base"><Shield className="h-5 w-5 text-indigo-300" /> DIRECTOR'S DRAW RECOMMENDATION</CardTitle></CardHeader>
            <CardContent>
              {pnl.net_profit > 0 ? (
                <div className="space-y-4">
                  <div className="bg-indigo-700/30 rounded-lg p-4">
                    <p className="text-indigo-200 text-sm mb-1 font-medium">Safe to draw ({pnl.safe_draw_pct}% of net profit)</p>
                    <p className="text-3xl font-black text-white" data-testid="pnl-safe-draw">{formatCurrency(pnl.safe_director_draw)}</p>
                  </div>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between"><span className="text-white">Net Profit</span><span className="text-white font-bold">{formatCurrency(pnl.net_profit)}</span></div>
                    <div className="flex justify-between"><span className="text-white">Director's Draw</span><span className="text-amber-700 font-bold">- {formatCurrency(pnl.safe_director_draw)}</span></div>
                    <div className="flex justify-between border-t border-indigo-600 pt-2"><span className="text-white">Retained in Business</span><span className="text-emerald-700 font-bold">{formatCurrency(pnl.retained_earnings)}</span></div>
                  </div>
                  <p className="text-white text-xs mt-2 leading-relaxed">
                    Drawing no more than {pnl.safe_draw_pct}% keeps your business healthy. The retained {formatCurrency(pnl.retained_earnings)} builds your cash reserve toward the recommended {formatCurrency(pnl.monthly_reserve_needed)} (2 months of expenses).
                  </p>
                </div>
              ) : (
                <div className="text-center py-4">
                  <p className="text-rose-700 font-bold text-lg mb-2">Not advisable to draw this month</p>
                  <p className="text-slate-600 text-sm">Operating at a loss of {formatCurrency(pnl.operating_profit)}. Focus on increasing revenue or reducing expenses before taking a draw.</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* YTD Summary */}
          <Card className="bg-gradient-to-br from-slate-50 to-slate-100 border-slate-200">
            <CardHeader className="pb-2"><CardTitle className="text-slate-900 flex items-center gap-2 text-base"><TrendingUp className="h-5 w-5 text-amber-700" /> YEAR TO DATE — FY {pnl.fy_label || pnl.month?.slice(0, 4)}</CardTitle></CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="flex justify-between items-center py-3 px-3 bg-white border border-slate-200 rounded">
                  <span className="text-slate-700 font-semibold">YTD Revenue</span>
                  <span className="text-amber-700 font-black text-lg">{formatCurrency(pnl.ytd_revenue)}</span>
                </div>
                <div className="flex justify-between items-center py-3 px-3 bg-white border border-slate-200 rounded">
                  <span className="text-slate-700 font-semibold">YTD Expenses</span>
                  <span className="text-orange-700 font-black text-lg">{formatCurrency(pnl.ytd_expenses)}</span>
                </div>
                <div className="flex justify-between items-center py-3 px-3 bg-white border border-slate-200 rounded">
                  <span className="text-slate-700 font-semibold">YTD Operating Profit</span>
                  <span className={`font-black text-lg ${pnl.ytd_operating >= 0 ? 'text-cyan-700' : 'text-rose-700'}`}>{formatCurrency(pnl.ytd_operating)}</span>
                </div>
                <div className="flex justify-between items-center py-3 px-3 bg-white border border-slate-200 rounded">
                  <span className="text-slate-700 font-semibold">YTD Tax (27%)</span>
                  <span className="text-slate-900 font-black text-lg">{formatCurrency(pnl.ytd_tax)}</span>
                </div>
                <div className={`flex justify-between items-center py-4 px-4 rounded-lg ${pnl.ytd_net >= 0 ? 'bg-green-800 border-2 border-green-400' : 'bg-red-800 border-2 border-red-400'}`}>
                  <span className="text-white font-black text-lg">YTD Net Profit</span>
                  <span className={`font-black text-2xl ${pnl.ytd_net >= 0 ? 'text-emerald-700' : 'text-rose-700'}`}>{formatCurrency(pnl.ytd_net)}</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Profit Margin Info */}
        <Card className="bg-gradient-to-br from-slate-50 to-slate-100 border-slate-200">
          <CardContent className="p-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
              <div>
                <p className="text-slate-700 text-sm mb-1 font-bold">Operating Margin</p>
                <p className={`text-3xl font-black ${pnl.revenue > 0 ? (pnl.operating_profit / pnl.revenue * 100 >= 0 ? 'text-cyan-700' : 'text-rose-700') : 'text-slate-900'}`}>
                  {pnl.revenue > 0 ? (pnl.operating_profit / pnl.revenue * 100).toFixed(1) : 0}%
                </p>
              </div>
              <div>
                <p className="text-slate-700 text-sm mb-1 font-bold">Net Margin</p>
                <p className={`text-3xl font-black ${pnl.revenue > 0 ? (pnl.net_profit / pnl.revenue * 100 >= 0 ? 'text-emerald-700' : 'text-rose-700') : 'text-slate-900'}`}>
                  {pnl.revenue > 0 ? (pnl.net_profit / pnl.revenue * 100).toFixed(1) : 0}%
                </p>
              </div>
              <div>
                <p className="text-slate-700 text-sm mb-1 font-bold">Expense Ratio</p>
                <p className="text-3xl font-black text-amber-700">
                  {pnl.revenue > 0 ? (pnl.expenses / pnl.revenue * 100).toFixed(1) : 0}%
                </p>
              </div>
              <div>
                <p className="text-slate-700 text-sm mb-1 font-bold">Cash Reserve Target</p>
                <p className="text-2xl font-black text-slate-900">{formatCurrency(pnl.monthly_reserve_needed)}</p>
                <p className="text-amber-700 text-xs font-medium">2 months expenses</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
